import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { ICmdAddress, TPositionRecords } from './interfaces';
import {
  POSITION_ADDRESS_KEY,
  POSITION_ALTITUDE_KEY,
  POSITION_DISTANCE_ADD,
  POSITION_DIST_CUMULATIVE_KEY,
  POSITION_IGNITION_ON_KEY,
  POSITION_SPEED_KEY,
  POSITION_GPS_TS_KEY,
  POSITION_TABLE_KEY,
  POSITION_IS_PRIVATE_KEY,
  POSITION_WAYPOINTS,
  POSITION_EVENTS,
  POSITION_ALARMS,
} from './constants';
import { ICmdTableColumn } from '../CmdTable/types';
import IconCell from '../CmdTable/TableCells/IconCell';
import DateTimeCell from '../CmdTable/TableCells/DateTimeCell';
import ThOnlySort from '../CmdTable/TableHeadCell/ThOnlySort';
import TextCell from '../CmdTable/TableCells/TextCell';
import LinkCell from '../CmdTable/TableCells/LinkCell';
import { columnsAtom } from '../CmdTable/CmdTableState';

interface IUseParkingTag {
  columns: ColumnDef<TPositionRecords>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
  tableKey: string;
}

export default function useTripPositions(rideId: string): IUseParkingTag {
  const { formatMessage: f } = useIntl();
  const tableKey = `${POSITION_TABLE_KEY}/${rideId}/positions`;
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);

  const columns = React.useMemo<ColumnDef<TPositionRecords>[]>(
    () => [
      {
        accessorKey: POSITION_GPS_TS_KEY,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_GPS_TS_KEY}
            title={f({ id: `table_list.columns.timestamp` })}
            // tooltip={f({ id: `table_list.columns.positions.${PARKING_POSITION_COLOR_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.gpsTs ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: POSITION_SPEED_KEY,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_SPEED_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_SPEED_KEY}` })}
            // tooltip={f({ id: `table_list.columns.positions.${PARKING_POSITION_COLOR_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.speed ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: POSITION_IGNITION_ON_KEY,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_IGNITION_ON_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_IGNITION_ON_KEY}` })}
            // tooltip={f({ id: `table_list.columns.positions.${PARKING_POSITION_COLOR_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.ignitionOn ? (
            <IconCell isIcon={info.getValue<boolean>()} icon="cmd-icon-active text-success" />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: POSITION_IS_PRIVATE_KEY,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_IGNITION_ON_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_IS_PRIVATE_KEY}` })}
            // tooltip={f({ id: `table_list.columns.positions.${PARKING_POSITION_COLOR_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.isPrivate ? (
            <IconCell isIcon={info.getValue<boolean>()} icon="cmd-icon-active text-success" />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: POSITION_ALTITUDE_KEY,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_SPEED_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_ALTITUDE_KEY}` })}
            // tooltip={f({ id: `table_list.columns.positions.${PARKING_POSITION_COLOR_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.altitude ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: POSITION_DISTANCE_ADD,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_SPEED_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_DISTANCE_ADD}` })}
            // tooltip={f({ id: `table_list.columns.positions.${PARKING_POSITION_COLOR_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.distanceAdd ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: POSITION_ADDRESS_KEY,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_SPEED_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_ADDRESS_KEY}` })}
            // tooltip={f({ id: `table_list.columns.positions.${PARKING_POSITION_COLOR_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.address ? (
            <LinkCell
              text={info.getValue<ICmdAddress>().name}
              link={info.row.original.address.link}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 360,
      },
      {
        accessorKey: POSITION_DIST_CUMULATIVE_KEY,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_SPEED_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_DIST_CUMULATIVE_KEY}` })}
            // tooltip={f({ id: `table_list.columns.positions.${PARKING_POSITION_COLOR_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.distanceCumulative ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },

      {
        accessorKey: POSITION_ALARMS,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_SPEED_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_ALARMS}` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.alarms ? (
            <TextCell text={info.getValue<string>()} align="left" />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: POSITION_EVENTS,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_SPEED_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_EVENTS}` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.events ? (
            <TextCell text={info.getValue<string>()} align="left" />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: POSITION_WAYPOINTS,
        header: (table) => (
          <ThOnlySort
            tableName={tableKey}
            id={POSITION_SPEED_KEY}
            title={f({ id: `table_list.columns.positions.${POSITION_WAYPOINTS}` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.waypoints ? (
            <TextCell text={info.getValue<string>()} align="left" />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
    ],
    [f, tableKey]
  );

  const columnOrder = React.useMemo(() => {
    return columnsTable.map((item) => item.key);
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc, column) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
    tableKey,
  };
}
